import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { onboardingTokens } from "../theme";
import { t } from "../../../locales/t";

/** Accept each policy only after its loaded document has been read to the end. */
const PolicyConsentDialog = ({ policy, onClose, onAccept }) => {
  const contentRef = useRef(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const evaluateScroll = useCallback(() => {
    const node = contentRef.current;
    if (!node || node.scrollHeight === 0) return;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 4) {
      setReadToEnd(true);
    }
  }, []);
  useEffect(() => {
    evaluateScroll();
  }, [policy, evaluateScroll]);
  const canAccept =
    Boolean(policy?.body?.trim()) &&
    Number.isInteger(policy?.revision) &&
    readToEnd;
  return (
    <Dialog open={Boolean(policy)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        {policy && t(`onboarding.policies.${policy.type}.title`)}
        <IconButton
          onClick={onClose}
          aria-label={t("onboarding.policies.close")}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        ref={contentRef}
        onScroll={evaluateScroll}
        data-testid="policy-scroll"
        tabIndex={0}
        role="region"
        aria-label={t("onboarding.policies.documentLabel")}
        dividers
        sx={{
          height: 320,
          "&:focus-visible": {
            outline: `3px solid ${onboardingTokens.navy}`,
            outlineOffset: -3,
          },
          "& p": { mb: 2, lineHeight: 1.65 },
          "& h1, & h2, & h3": {
            fontFamily: onboardingTokens.fontHeading,
            mt: 3,
            mb: 1,
            lineHeight: 1.3,
          },
          "& h1": { fontSize: "1.25rem" },
          "& h2": { fontSize: "1.125rem" },
          "& h3": { fontSize: "1rem" },
          "& ul, & ol": { pl: 3, mb: 2 },
          "& li": { mb: 0.5, lineHeight: 1.6 },
          "& a": { color: onboardingTokens.teal },
          "& a, & code": { overflowWrap: "anywhere" },
          "& table": { width: "100%", borderCollapse: "collapse", mb: 2 },
          "& th, & td": {
            border: `1px solid ${onboardingTokens.track}`,
            p: 1,
            textAlign: "left",
          },
        }}
      >
        {policy && (
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node: _node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {policy.body}
          </Markdown>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!readToEnd && <span>{t("onboarding.policies.scrollPrompt")}</span>}
        <Button onClick={onClose}>{t("onboarding.policies.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!canAccept}
          onClick={() => onAccept(policy.type, policy.revision)}
        >
          {t("onboarding.policies.accept")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyConsentDialog;
